import CustomDrawer from "@/components/drawer";
import {
  WorkScheduleListData,
  UserWorkScheduleAppointmentDto,
} from "@/models/club";
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCircleFilled, IconPencil, IconTrash } from "@tabler/icons-react";
import moment from "moment-jalaali";
import { useEffect, useState } from "react";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DatePicker from "react-multi-date-picker";
import AddTimeSlot from "./AddTimeSlot";
import {
  AddWorkScheduleCommandForm,
  WeekDays,
  WeekDaysFa,
  formatTime,
  schemaAddWorkScheduleCommand,
} from "./type";
import { useCreateShiftSchedule } from "@/services/shiftSchedule/utils";
import { useForm, yupResolver } from "@mantine/form";
import "react-multi-date-picker/styles/layouts/mobile.css";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";

type Props = {
  opened: boolean;
  close: () => void;
  selectedTeam: UserWorkScheduleAppointmentDto;
  selectedDate: Date[];
  setSelectedDate: (date: Date[]) => void;
  shiftSchedule: WorkScheduleListData;
  refetchShiftSchedule: () => void;
};

function AddShift({
  opened,
  close,
  selectedTeam,
  selectedDate,
  shiftSchedule,
  setSelectedDate,
  refetchShiftSchedule,
}: Props) {
  const [openedEdit, { open: openEdit, close: closeEdit }] =
    useDisclosure(false);

  const { mutate: createShiftSchedule, isLoading } = useCreateShiftSchedule();
  const navigate = useNavigate();
  const form = useForm<AddWorkScheduleCommandForm>({
    initialValues: {
      fromDateObject: undefined,
      toDateObject: undefined,
      merchantUserId: selectedTeam?.user?.id,
      weekDays: [],
      fromDate: undefined,
      toDate: undefined,
    },
    validate: yupResolver(schemaAddWorkScheduleCommand),
  });

  const [selectedWeekDay, setSelectedWeekDay] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    const selectedTeamShiftSchedule = shiftSchedule.find(
      (x) => x.user?.id === selectedTeam?.user?.id
    );

    const weekDays = selectedTeamShiftSchedule?.workScheduleCalendar?.map(
      (x) => ({
        weekDay:
          WeekDaysFa[
          moment(x.availableDate)
            .locale("fa")
            .format("dd") as keyof typeof WeekDaysFa
          ],
        timeSlots: x.timeSlots?.map((timeSlot) => ({
          startTime: {
            hour: moment(timeSlot?.startTime, "HH:mm:ss").hour(),
            minute: moment(timeSlot?.startTime, "HH:mm:ss").minute(),
          },
          endTime: {
            hour: moment(timeSlot.endTime, "HH:mm:ss").hour(),
            minute: moment(timeSlot.endTime, "HH:mm:ss").minute(),
          },
        })),
      })
    );

    if (selectedDate?.[0] && selectedDate?.[1]) {
      form.setFieldValue("fromDateObject", moment(selectedDate?.[0]));
      form.setFieldValue("toDateObject", moment(selectedDate?.[1]));
    }
    form.setFieldValue("weekDays", weekDays);

    return () => {
      form.setFieldValue("weekDays", []);
    };
  }, [setSelectedDate, shiftSchedule, selectedDate, selectedTeam?.user?.id]);

  const submit = (values: AddWorkScheduleCommandForm) => {
    const fromMoment = moment(values.fromDateObject).format("YYYY-MM-DD");
    const toMoment = moment(values.toDateObject).format("YYYY-MM-DD");

    createShiftSchedule(
      {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        fromDate: fromMoment,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        toDate: toMoment,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        weekDays: values?.weekDays?.map((x) => ({
          weekDay: x.weekDay,
          timeSlots: x.timeSlots?.map((y) => ({
            startTime: `${String(y.startTime?.hour ?? 0).padStart(
              2,
              "0"
            )}:${String(y.startTime?.minute ?? 0).padStart(2, "0")}:${String(
              y.startTime?.second ?? 0
            ).padStart(2, "0")}`,
            endTime: `${String(y.endTime?.hour ?? 0).padStart(2, "0")}:${String(
              y.endTime?.minute ?? 0
            ).padStart(2, "0")}:${String(y.endTime?.second ?? 0).padStart(
              2,
              "0"
            )}`,
          })),
        })),
        merchantUserId: selectedTeam?.user?.id,
      },
      {
        onSuccess() {
          notifications.show({
            message: "عملیات با موفقیت صورت پذیرفت",
            color: "green",
          });
          refetchShiftSchedule();
          navigate(-1);
        },
      }
    );
  };
  return (
    <>
      <CustomDrawer
        opened={opened}
        onClose={close}
        close={close}
        position="bottom"
        radius="md"
        custom="add-shift-schedule"
        size="lg"
        title={
          <Title order={6}>
            تعریف شیفت کاری برای ({selectedTeam?.user?.firstName})
          </Title>
        }
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: "100vh",
          }}
        >
          <Box style={{ flex: 1 }} px="md" mb="lg">
            <Text size="xs" c="dimmed">
              شیفت‌های هفتگی، سفارشی را تنظیم کنید. تغییرات ذخیره شده برای همه
              شیفت‌های آینده برای دوره انتخاب شده اعمال خواهد شد.
            </Text>
            <form
              onSubmit={form.onSubmit(submit)}
              name="add-schedule"
              className="w-100"
              id="add-schedule"
            >
              <Stack mt="lg">
                <DatePicker
                  className="rmdp-mobile"
                  calendar={persian}
                  locale={persian_fa}
                  value={form.values.fromDateObject?.toDate()}
                  onChange={(date) => {
                    form.setFieldValue(
                      "fromDateObject",
                      date?.toDate?.() ? moment(date?.toDate?.()) : undefined
                    );
                  }}
                  render={
                    <TextInput
                      size="lg"
                      label="تاریخ شروع"
                      withAsterisk
                      placeholder="تاریخ را انتخاب کنید"
                      error={form.errors.fromDateObject}
                      styles={{
                        input: {
                          direction: "rtl",
                          textAlign: "right",
                        },
                        error: {
                          fontSize: "0.8rem",
                        },
                      }}
                      readOnly
                    />
                  }
                />

                <DatePicker
                  calendar={persian}
                  className="rmdp-mobile"
                  locale={persian_fa}
                  value={form.values.toDateObject?.toDate()}
                  onChange={(date) => {
                    form.setFieldValue(
                      "toDateObject",
                      date?.toDate?.() ? moment(date?.toDate?.()) : undefined
                    );
                  }}
                  render={
                    <TextInput
                      size="lg"
                      label="تاریخ پایان"
                      withAsterisk
                      placeholder="تاریخ را انتخاب کنید"
                      error={form.errors.toDateObject}
                      styles={{
                        input: {
                          direction: "rtl",
                          textAlign: "right",
                        },
                        error: {
                          fontSize: "0.8rem",
                        },
                      }}
                      readOnly
                    />
                  }
                />
                <Stack mt="md">
                  <Stack gap="0">
                    <Title order={5} c={form.errors.weekDays ? "red" : ""}>
                      روزهای هفته
                    </Title>

                    {form.errors.weekDays ? (
                      <Text
                        size="xs"
                        c={form.errors.weekDays ? "red" : "gray.5"}
                      >
                        {form.errors.weekDays}
                      </Text>
                    ) : null}
                  </Stack>

                  <Stack gap="md">
                    {[1, 2, 3, 4, 5, 6, 7].map((item, index) => (
                      <>
                        <Group justify="space-between">
                          <Group gap="5">
                            <IconCircleFilled
                              size="15"
                              color={
                                form.values?.weekDays?.find(
                                  (d) => d.weekDay === item
                                )?.timeSlots?.length
                                  ? "var(--mantine-color-green-5)"
                                  : "var(--mantine-color-gray-5)"
                              }
                            />
                            <Text size="sm" fw="500">
                              {WeekDays[item as keyof typeof WeekDays]}
                            </Text>
                          </Group>
                          <Group gap="5">
                            <Stack gap="0">
                              {form.values?.weekDays?.find(
                                (d) => d.weekDay === item
                              )?.timeSlots?.length ? (
                                form.values?.weekDays
                                  ?.find((d) => d.weekDay === item)
                                  ?.timeSlots?.map((timeSlot) => (
                                    <Text>
                                      {formatTime(
                                        timeSlot.endTime?.hour,
                                        timeSlot.endTime?.minute
                                      )}
                                      -{" "}
                                      {formatTime(
                                        timeSlot.startTime?.hour,
                                        timeSlot.startTime?.minute
                                      )}{" "}
                                    </Text>
                                  ))
                              ) : (
                                <Text size="xs" c="gray.5">
                                  کار نمیکنم
                                </Text>
                              )}
                            </Stack>
                            {form.values.weekDays?.find(
                              (d) => d.weekDay === item
                            )?.timeSlots?.length ? (
                              <ActionIcon
                                variant="subtle"
                                color="dark.9"
                                onClick={() => {
                                  const weekDays = form.values.weekDays ?? [];
                                  const existingWeekDayIndex =
                                    weekDays.findIndex(
                                      (d) => d.weekDay === item
                                    );
                                  if (existingWeekDayIndex !== -1) {
                                    const updatedWeekDays = [...weekDays];
                                    updatedWeekDays.splice(
                                      existingWeekDayIndex,
                                      1
                                    );
                                    form.setFieldValue(
                                      "weekDays",
                                      updatedWeekDays
                                    );
                                  }
                                }}
                              >
                                <IconTrash stroke={1} />
                              </ActionIcon>
                            ) : null}
                            <ActionIcon
                              variant="subtle"
                              color="dark.9"
                              onClick={() => {
                                setSelectedWeekDay(item);
                                openEdit();
                              }}
                            >
                              <IconPencil stroke={1} />
                            </ActionIcon>
                          </Group>
                        </Group>
                        {index !== 6 && <Divider />}
                      </>
                    ))}
                  </Stack>
                </Stack>
              </Stack>
            </form>
          </Box>
          <Card
            styles={{
              root: {
                position: "sticky",
                bottom: 0,
                zIndex: 10,
              },
            }}
            withBorder
            radius={0}
          >
            <Button
              type="submit"
              size="lg"
              fullWidth

              form="add-schedule"
              loading={isLoading}
            >
              افزودن
            </Button>
          </Card>
        </Box>
      </CustomDrawer>

      <AddTimeSlot
        opened={openedEdit}
        close={closeEdit}
        selectedWeekDay={selectedWeekDay as number}
        selectedTeam={selectedTeam}
        addTimeSlotToDay={(timeSlots) => {
          const weekDays = form.values.weekDays ?? [];
          const existingWeekDayIndex = weekDays.findIndex(
            (d) => d.weekDay === timeSlots.weekDay
          );
          if (existingWeekDayIndex !== -1) {
            // Update existing weekday's timeslots
            const updatedWeekDays = [...weekDays];
            updatedWeekDays[existingWeekDayIndex] = timeSlots;
            form.setFieldValue("weekDays", updatedWeekDays);
          } else {
            // Add new weekday
            form.setFieldValue("weekDays", [...weekDays, timeSlots]);
          }
        }}
      />
    </>
  );
}

export default AddShift;
